import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import Alert, { AlertSize, AlertVariant } from '../../atoms/alert/Alert';
import Button, { ButtonSize, ButtonVariant } from '../../atoms/buttons/button/Button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../atoms/form/Form';
import Input from '../../atoms/inputs/input/Input';
import type { QuestionValue, Survey } from './types';

const QUESTION_TYPES = [
  { value: 'radio', label: 'Radio' },
  { value: 'textArea', label: 'Zone de texte' },
  { value: 'select', label: 'Liste déroulante' },
  { value: 'checkbox', label: 'Case à cocher' },
] as const;

type FormValues = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  questions: QuestionValue[];
};

type Props = {
  survey?: Survey;
  initialQuestions?: QuestionValue[];
  onSuccess: () => void;
  onCancel: () => void;
};

function toApiDate(localDate: string): string {
  const withSpace = localDate.replace('T', ' ');
  return withSpace.length <= 16 ? withSpace + ':00' : withSpace.substring(0, 19);
}

function toFormDate(apiDate: string): string {
  return apiDate.slice(0, 16).replace(' ', 'T');
}

export function SurveyForm({ survey, initialQuestions, onSuccess, onCancel }: Props) {
  const isEdit = !!survey;

  const methods = useForm<FormValues>({
    defaultValues: {
      title: survey?.theme ?? '',
      description: survey?.description ?? '',
      startDate: survey ? toFormDate(survey.date_start) : '',
      endDate: survey ? toFormDate(survey.date_end) : '',
      questions: initialQuestions ?? [{ text: '', type: 'radio' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'questions',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    setSubmitError(null);

    const body = {
      ...(isEdit ? { id: survey.id } : {}),
      title: values.title,
      description: values.description,
      startDate: toApiDate(values.startDate),
      endDate: toApiDate(values.endDate),
      questions: values.questions.map((q) => ({ text: q.text, type: q.type })),
    };

    try {
      const r = await fetch(isEdit ? '/sondage/updateSurvey' : '/sondage/createSurvey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!r.ok) {
        const text = await r.text();
        setSubmitError(text || 'Une erreur est survenue.');
        setSubmitting(false);
        return;
      }

      onSuccess();
    } catch {
      setSubmitError('Erreur réseau.');
      setSubmitting(false);
    }
  }

  return (
    <Form {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant={ButtonVariant.GHOST}
            size={ButtonSize.SM}
            onClick={onCancel}
          >
            ← Retour
          </Button>
          <h2 className="text-base font-semibold text-neutral-800">
            {isEdit ? 'Modifier le sondage' : 'Nouveau sondage'}
          </h2>
        </div>

        {submitError && (
          <Alert variant={AlertVariant.DESTRUCTIVE} size={AlertSize.SMALL}>
            {submitError}
          </Alert>
        )}

        <FormField
          control={methods.control}
          name="title"
          rules={{ required: 'Le titre est requis' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Titre</FormLabel>
              <FormControl>
                <Input.Root>
                  <Input.Input placeholder="Titre du sondage" {...field} />
                </Input.Root>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={methods.control}
          name="description"
          rules={{ required: 'La description est requise' }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input.Root>
                  <Input.Input placeholder="Description du sondage" {...field} />
                </Input.Root>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-4">
          <FormField
            control={methods.control}
            name="startDate"
            rules={{ required: 'La date de début est requise' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date de début</FormLabel>
                <FormControl>
                  <Input.Root>
                    <Input.Input type="datetime-local" {...field} />
                  </Input.Root>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={methods.control}
            name="endDate"
            rules={{ required: 'La date de fin est requise' }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date de fin</FormLabel>
                <FormControl>
                  <Input.Root>
                    <Input.Input type="datetime-local" {...field} />
                  </Input.Root>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-lg font-medium text-neutral-700">Questions / Options de réponse</p>
          {fields.map((field, index) => (
            <div key={field.id} className="flex items-start gap-2">
              <span className="mt-2 w-5 shrink-0 text-right text-lg text-neutral-400">
                {index + 1}.
              </span>

              <FormField
                control={methods.control}
                name={`questions.${index}.text`}
                rules={{ required: 'Ce champ est requis' }}
                render={({ field: f }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input.Root>
                        <Input.Input placeholder="Question ou option de réponse" {...f} />
                      </Input.Root>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name={`questions.${index}.type`}
                render={({ field: f }) => (
                  <FormItem className="w-32 shrink-0 space-y-0">
                    <FormControl>
                      <select
                        {...f}
                        className="h-8 w-full rounded border-0 bg-white px-2.5 text-lg font-medium text-neutral-700 ring-1 ring-inset ring-neutral-300 focus:outline-none focus:ring-1 focus:ring-neutral-500"
                      >
                        {QUESTION_TYPES.map(({ value, label }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button
                type="button"
                variant={ButtonVariant.GHOST}
                size={ButtonSize.ICON}
                disabled={fields.length === 1}
                onClick={() => remove(index)}
                className="mt-0.5 shrink-0 text-neutral-400 hover:text-red-500"
              >
                ×
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant={ButtonVariant.GHOST}
            size={ButtonSize.SM}
            className="self-start"
            onClick={() => append({ text: '', type: 'radio' })}
          >
            + Ajouter une option
          </Button>
        </div>

        <div className="flex gap-2 pt-2 border-t border-neutral-100">
          <Button
            type="submit"
            variant={ButtonVariant.BRAND}
            size={ButtonSize.MD}
            disabled={submitting}
          >
            {submitting ? 'Enregistrement...' : isEdit ? 'Enregistrer' : 'Créer le sondage'}
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.DEFAULT}
            size={ButtonSize.MD}
            onClick={onCancel}
          >
            Annuler
          </Button>
        </div>
      </form>
    </Form>
  );
}
